/**
 * Gerenciador de Termos Customizados
 * Permite ao usuário criar, editar e excluir termos customizados em 3 escopos:
 * GLOBAL (todos os arquivos), FOLDER (pasta), FILE (arquivo específico).
 * Também sugere termos a partir da análise de IA.
 */

import React, { useState, useCallback, useMemo, useImperativeHandle, forwardRef } from 'react'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  Switch,
  FormControlLabel,
  Alert,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import SaveIcon from '@mui/icons-material/Save'
import CloseIcon from '@mui/icons-material/Close'
import PublicIcon from '@mui/icons-material/Public'
import FolderIcon from '@mui/icons-material/Folder'
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import type { TermScope, CustomTermConfig } from '../../../types/settings'
import { useCustomTermsScope, useTermSuggestions } from '../../../hooks/useCustomTerms'
import { getCategoryColor } from '../../../constants/termColors'

export interface CustomTermsManagerHandle {
  openAdd: (term?: string) => void
}

interface CustomTermsManagerProps {
  fileId: string
  folderId?: string | null
  /** Modo compacto para uso dentro do painel lateral */
  compact?: boolean
}

type ScopeTab = 'GLOBAL' | 'FOLDER' | 'FILE'

const SCOPE_CONFIG: Record<ScopeTab, { label: string; icon: React.ReactNode; description: string; tip: string }> = {
  GLOBAL: {
    label: 'Todos',
    icon: <PublicIcon fontSize="small" />,
    description: 'Aplicados em todos os seus arquivos',
    tip: 'Ideal para termos que você usa frequentemente em várias transcrições.',
  },
  FOLDER: {
    label: 'Pasta',
    icon: <FolderIcon fontSize="small" />,
    description: 'Aplicados aos arquivos desta pasta',
    tip: 'Útil para termos específicos de um caso ou processo.',
  },
  FILE: {
    label: 'Arquivo',
    icon: <InsertDriveFileIcon fontSize="small" />,
    description: 'Apenas neste arquivo',
    tip: 'Para termos únicos desta transcrição específica.',
  },
}

const EMPTY_TERM: CustomTermConfig = {
  term: '',
  pattern: '',
  category: '',
  description: '',
  enabled: true,
  priority: 0,
}

const CustomTermsManager = forwardRef<CustomTermsManagerHandle, CustomTermsManagerProps>(
  function CustomTermsManager({ fileId, folderId, compact = false }, ref) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const [activeScope, setActiveScope] = useState<ScopeTab>('GLOBAL')
  const [advancedOpen, setAdvancedOpen] = useState(false)
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [editingTerm, setEditingTerm] = useState<CustomTermConfig>(EMPTY_TERM)
  const [editingIndex, setEditingIndex] = useState<number | null>(null)
  const [hasChanges, setHasChanges] = useState(false)
  const [saveSuccess, setSaveSuccess] = useState(false)

  // Scope-based CRUD hooks
  const globalScope = useCustomTermsScope('GLOBAL')
  const folderScope = useCustomTermsScope('FOLDER', folderId)
  const fileScope = useCustomTermsScope('FILE', fileId)

  // AI suggestions hook
  const { suggestions, loading: suggestionsLoading, loadSuggestions } = useTermSuggestions(fileId)

  const currentScope = useMemo(() => {
    switch (activeScope) {
      case 'GLOBAL': return globalScope
      case 'FOLDER': return folderScope
      case 'FILE': return fileScope
    }
  }, [activeScope, globalScope, folderScope, fileScope])

  const currentTerms: CustomTermConfig[] = useMemo(
    () => currentScope.data?.terms || [],
    [currentScope.data]
  )

  const scopeId = activeScope === 'FOLDER' ? folderId : activeScope === 'FILE' ? fileId : undefined

  const folderAvailable = !!folderId

  // Imperativo: permite ao pai abrir o dialog de adição com termo pré-preenchido
  useImperativeHandle(ref, () => ({
    openAdd: (term?: string) => {
      setEditingTerm({ ...EMPTY_TERM, term: term || '' })
      setEditingIndex(null)
      setEditDialogOpen(true)
    },
  }), [])

  // ---- Handlers ----

  const handleScopeChange = useCallback((_: React.SyntheticEvent, value: ScopeTab) => {
    if (value === 'FOLDER' && !folderAvailable) return
    setActiveScope(value)
    setHasChanges(false)
    setSaveSuccess(false)
  }, [folderAvailable])

  const handleOpenAdd = useCallback(() => {
    setEditingTerm({ ...EMPTY_TERM })
    setEditingIndex(null)
    setEditDialogOpen(true)
  }, [])

  const handleOpenEdit = useCallback((term: CustomTermConfig, index: number) => {
    setEditingTerm({ ...term })
    setEditingIndex(index)
    setEditDialogOpen(true)
  }, [])

  const handleCloseDialog = useCallback(() => {
    setEditDialogOpen(false)
    setEditingTerm(EMPTY_TERM)
    setEditingIndex(null)
  }, [])

  const [saveError, setSaveError] = useState<string | null>(null)

  const handleSaveTerm = useCallback(async () => {
    if (!editingTerm.term.trim()) return

    const updated = [...currentTerms]
    const termToSave: CustomTermConfig = {
      ...editingTerm,
      term: editingTerm.term.trim(),
      pattern: editingTerm.pattern.trim() || editingTerm.term.trim(),
      category: editingTerm.category.trim(),
      description: editingTerm.description.trim(),
    }

    if (editingIndex !== null) {
      updated[editingIndex] = termToSave
    } else {
      updated.push(termToSave)
    }

    try {
      setSaveError(null)
      await currentScope.save(updated)
      setHasChanges(false)
      setSaveSuccess(true)
      setTimeout(() => setSaveSuccess(false), 3000)
      handleCloseDialog()
    } catch (err) {
      console.error('Erro ao salvar termo:', err)
      setSaveError('Erro ao salvar termo. Tente novamente.')
      setTimeout(() => setSaveError(null), 5000)
    }
  }, [editingTerm, editingIndex, currentTerms, currentScope, handleCloseDialog])

  const handleDeleteTerm = useCallback(async (index: number) => {
    const updated = currentTerms.filter((_, i) => i !== index)
    try {
      setSaveError(null)
      await currentScope.save(updated)
      setSaveSuccess(true)
      setTimeout(() => setSaveSuccess(false), 3000)
    } catch (err) {
      console.error('Erro ao excluir termo:', err)
      setSaveError('Erro ao excluir termo. Tente novamente.')
      setTimeout(() => setSaveError(null), 5000)
    }
  }, [currentTerms, currentScope])

  const handleToggleTerm = useCallback(async (index: number) => {
    const updated = [...currentTerms]
    updated[index] = { ...updated[index], enabled: !updated[index].enabled }
    try {
      await currentScope.save(updated)
    } catch (err) {
      console.error('Erro ao alternar termo:', err)
    }
  }, [currentTerms, currentScope])

  const handleImportSuggestion = useCallback(async (suggestion: CustomTermConfig) => {
    const exists = currentTerms.some(
      t => t.term.toLowerCase() === suggestion.term.toLowerCase()
    )
    if (exists) return

    const updated = [...currentTerms, { ...suggestion, enabled: true }]
    try {
      setSaveError(null)
      await currentScope.save(updated)
      setSaveSuccess(true)
      setTimeout(() => setSaveSuccess(false), 3000)
    } catch (err) {
      console.error('Erro ao importar sugestão:', err)
      setSaveError('Erro ao importar sugestão. Tente novamente.')
      setTimeout(() => setSaveError(null), 5000)
    }
  }, [currentTerms, currentScope])

  const handleImportAllSuggestions = useCallback(async () => {
    const existingTermsLower = new Set(currentTerms.map(t => t.term.toLowerCase()))
    const newTerms = suggestions.filter(s => !existingTermsLower.has(s.term.toLowerCase()))
    if (newTerms.length === 0) return

    const updated = [...currentTerms, ...newTerms.map(t => ({ ...t, enabled: true }))]
    try {
      setSaveError(null)
      await currentScope.save(updated)
      setSaveSuccess(true)
      setTimeout(() => setSaveSuccess(false), 3000)
    } catch (err) {
      console.error('Erro ao importar sugestões:', err)
      setSaveError('Erro ao importar sugestões. Tente novamente.')
      setTimeout(() => setSaveError(null), 5000)
    }
  }, [currentTerms, suggestions, currentScope])

  // ---- Render helpers ----

  const renderTermItem = (term: CustomTermConfig, index: number) => {
    const color = getCategoryColor(term.category)
    return (
      <Paper
        key={`${term.term}-${index}`}
        variant="outlined"
        sx={{
          p: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          opacity: term.enabled === false ? 0.5 : 1,
          transition: 'opacity 0.2s',
        }}
      >
        <Switch
          size="small"
          checked={term.enabled !== false}
          onChange={() => handleToggleTerm(index)}
        />

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="body2" fontWeight={600} noWrap>
              {term.term}
            </Typography>
            {term.category && (
              <Chip
                label={color.label}
                size="small"
                sx={{ bgcolor: color.bg, color: color.text, fontSize: '0.7rem', height: 20 }}
              />
            )}
            {(term.priority ?? 0) > 0 && (
              <Chip
                label={`P${term.priority}`}
                size="small"
                variant="outlined"
                sx={{ fontSize: '0.65rem', height: 18 }}
              />
            )}
          </Box>
          {term.description && (
            <Typography variant="caption" color="text.secondary" noWrap>
              {term.description}
            </Typography>
          )}
        </Box>

        <Tooltip title="Editar">
          <IconButton size="small" onClick={() => handleOpenEdit(term, index)}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Excluir">
          <IconButton size="small" color="error" onClick={() => handleDeleteTerm(index)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Paper>
    )
  }

  const renderSuggestions = () => {
    if (suggestions.length === 0 && !suggestionsLoading) {
      return (
        <Box sx={{ textAlign: 'center', py: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Clique em "Sugerir da IA" para obter sugestões de termos a partir da análise deste arquivo.
          </Typography>
        </Box>
      )
    }

    const existingTermsLower = new Set(currentTerms.map(t => t.term.toLowerCase()))

    return (
      <Stack spacing={1}>
        {suggestions.map((suggestion, i) => {
          const alreadyAdded = existingTermsLower.has(suggestion.term.toLowerCase())
          const color = getCategoryColor(suggestion.category)
          return (
            <Paper
              key={`sug-${i}`}
              variant="outlined"
              sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#fafafa' }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="body2" fontWeight={500}>
                    {suggestion.term}
                  </Typography>
                  {suggestion.category && (
                    <Chip
                      label={color.label}
                      size="small"
                      sx={{ bgcolor: color.bg, color: color.text, fontSize: '0.7rem', height: 20 }}
                    />
                  )}
                </Box>
                {suggestion.description && (
                  <Typography variant="caption" color="text.secondary">
                    {suggestion.description}
                  </Typography>
                )}
              </Box>
              <Tooltip title={alreadyAdded ? 'Já adicionado' : 'Adicionar'}>
                <span>
                  <IconButton
                    size="small"
                    color="primary"
                    disabled={alreadyAdded}
                    onClick={() => handleImportSuggestion(suggestion)}
                  >
                    <AddIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Paper>
          )
        })}
      </Stack>
    )
  }

  return (
    <Box>
      {/* Cabeçalho — oculto em modo compacto (o ToolsSidePanel já mostra o título) */}
      {!compact && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
            🏷️ Termos Customizados
          </Typography>
        </Box>
      )}

      {/* Feedback */}
      {saveSuccess && (
        <Alert severity="success" sx={{ py: 0, fontSize: '0.75rem', mb: 1 }}>Salvo!</Alert>
      )}
      {saveError && (
        <Alert severity="error" sx={{ py: 0, fontSize: '0.75rem', mb: 1 }}>{saveError}</Alert>
      )}

      {/* Seletor de escopo */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5, flexWrap: 'wrap' }}>
        {(Object.entries(SCOPE_CONFIG) as [ScopeTab, typeof SCOPE_CONFIG[ScopeTab]][]).map(([scope, cfg]) => {
          const count = scope === 'GLOBAL' ? globalScope.data?.terms?.length || 0
            : scope === 'FOLDER' ? folderScope.data?.terms?.length || 0
              : fileScope.data?.terms?.length || 0
          const disabled = scope === 'FOLDER' && !folderAvailable
          return (
            <Chip
              key={scope}
              icon={cfg.icon as React.ReactElement}
              label={`${cfg.label} (${count})`}
              size="small"
              disabled={disabled}
              variant={activeScope === scope ? 'filled' : 'outlined'}
              color={activeScope === scope ? 'primary' : 'default'}
              onClick={() => !disabled && handleScopeChange(null as any, scope)}
              sx={{ fontSize: '0.75rem' }}
            />
          )
        })}
      </Box>

      {/* Dica do escopo selecionado */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 1.5, px: 0.5 }}>
        <HelpOutlineIcon sx={{ fontSize: 14, color: 'text.disabled', mt: 0.3 }} />
        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4 }}>
          {SCOPE_CONFIG[activeScope].tip}
        </Typography>
      </Box>

      {currentScope.loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <>
          {/* Ações */}
          <Box sx={{ display: 'flex', gap: 0.5, mb: 1.5, flexWrap: 'wrap' }}>
            <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={handleOpenAdd} sx={{ textTransform: 'none', fontSize: '0.8rem' }}>
              Adicionar
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={suggestionsLoading ? <CircularProgress size={14} /> : <AutoAwesomeIcon />}
              onClick={loadSuggestions}
              disabled={suggestionsLoading}
              sx={{ textTransform: 'none', fontSize: '0.8rem' }}
            >
              Sugerir da IA
            </Button>
            {suggestions.length > 0 && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<ContentCopyIcon />}
                onClick={handleImportAllSuggestions}
                sx={{ textTransform: 'none', fontSize: '0.8rem' }}
              >
                Importar ({suggestions.filter(s => !currentTerms.some(t => t.term.toLowerCase() === s.term.toLowerCase())).length})
              </Button>
            )}
          </Box>

          {/* Lista de termos ou onboarding */}
          {currentTerms.length === 0 ? (
            <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', bgcolor: '#fafafa' }}>
              <AutoAwesomeIcon sx={{ fontSize: 28, color: '#bdbdbd', mb: 0.5 }} />
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontSize: '0.85rem' }}>
                Nenhum termo cadastrado
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                Termos permitem destacar palavras importantes na transcrição com cores.
              </Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={suggestionsLoading ? <CircularProgress size={14} /> : <AutoAwesomeIcon />}
                onClick={loadSuggestions}
                disabled={suggestionsLoading}
                sx={{ textTransform: 'none' }}
              >
                Sugerir termos da IA
              </Button>
            </Paper>
          ) : (
            <Stack spacing={0.75} sx={{ mb: 1.5 }}>
              {currentTerms.map((term, i) => renderTermItem(term, i))}
            </Stack>
          )}

          {/* Sugestões da IA */}
          {suggestions.length > 0 && (
            <>
              <Divider sx={{ my: 1.5 }} />
              <Typography variant="caption" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>
                💡 Sugestões da IA ({suggestions.length})
              </Typography>
              {renderSuggestions()}
            </>
          )}
        </>
      )}

      {currentScope.saving && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
          <CircularProgress size={14} />
          <Typography variant="caption" color="text.secondary">Salvando...</Typography>
        </Box>
      )}

      {/* Dialog de Edição/Adição */}
      <Dialog
        open={editDialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            {editingIndex !== null ? 'Editar Termo' : 'Novo Termo'}
          </Typography>
          <IconButton onClick={handleCloseDialog} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {/* Campos principais */}
            <TextField
              label="Termo"
              value={editingTerm.term}
              onChange={e => setEditingTerm(prev => ({ ...prev, term: e.target.value }))}
              fullWidth
              required
              size="small"
              placeholder="Ex: habeas corpus"
              helperText="Texto que será destacado na transcrição"
              autoFocus
            />
            <TextField
              label="Categoria"
              value={editingTerm.category}
              onChange={e => setEditingTerm(prev => ({ ...prev, category: e.target.value }))}
              fullWidth
              size="small"
              placeholder="Ex: processual, penal, geral"
              helperText="Define a cor do destaque (ex: processual = azul, penal = vermelho)"
            />

            {/* Seção avançada colapsável */}
            <Box>
              <Button
                size="small"
                onClick={() => setAdvancedOpen(!advancedOpen)}
                startIcon={<ExpandMoreIcon sx={{ transform: advancedOpen ? 'rotate(180deg)' : 'none', transition: '0.2s' }} />}
                sx={{ textTransform: 'none', color: 'text.secondary', fontSize: '0.8rem', px: 0.5 }}
              >
                Opções avançadas
              </Button>
              <Collapse in={advancedOpen}>
                <Stack spacing={2} sx={{ mt: 1 }}>
                  <TextField
                    label="Padrão (regex)"
                    value={editingTerm.pattern}
                    onChange={e => setEditingTerm(prev => ({ ...prev, pattern: e.target.value }))}
                    fullWidth
                    size="small"
                    placeholder="Ex: habeas\s+corpus"
                    helperText="Regex para busca. Se vazio, usa o termo como texto exato."
                  />
                  <TextField
                    label="Descrição"
                    value={editingTerm.description}
                    onChange={e => setEditingTerm(prev => ({ ...prev, description: e.target.value }))}
                    fullWidth
                    size="small"
                    multiline
                    rows={2}
                    placeholder="Breve descrição do termo"
                  />
                  <TextField
                    label="Prioridade"
                    type="number"
                    value={editingTerm.priority ?? 0}
                    onChange={e => setEditingTerm(prev => ({ ...prev, priority: parseInt(e.target.value, 10) || 0 }))}
                    fullWidth
                    size="small"
                    helperText="Maior prioridade = destacado primeiro"
                    inputProps={{ min: 0, max: 100 }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={editingTerm.enabled !== false}
                        onChange={e => setEditingTerm(prev => ({ ...prev, enabled: e.target.checked }))}
                        size="small"
                      />
                    }
                    label={<Typography variant="body2">Ativo</Typography>}
                  />
                </Stack>
              </Collapse>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={currentScope.saving} size="small">Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleSaveTerm}
            disabled={!editingTerm.term.trim() || currentScope.saving}
            startIcon={currentScope.saving ? <CircularProgress size={14} /> : <SaveIcon />}
            size="small"
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
})

export default CustomTermsManager
